import { useState, useRef } from "react";
import { Plus, Link, Trash2 } from "lucide-react";
import { Box, Text, Button, Flex } from "@radix-ui/themes";
import { Field, RelationshipFieldDatabaseType } from "@/types/breeze/Field";
import { IEntity } from "@/types/breeze/Entity";
import { IAny } from "@/types/breeze/Any";
import { useEntities } from "@/hooks/useCommonQueries";
import ManagedEntityGrid from "@/components/entity/ManagedEntityGrid";
import EntityDetailView from "@/components/entity/EntityDetailView";
import SideDrawer from "@/components/ui/SideDrawer";
import EntityFormV2, {
  EntityFormHandle,
} from "@/components/entity/EntityFormV2";
import { formatColumnName, singularize } from "@/utils/text";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { anyService } from "@/services/breeze/AnyService";
import { useAuth } from "@/providers/AuthProvider";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { useFormikContext } from "formik";

interface RelationshipCollectionFieldProps {
  field: Field;
  entity: IEntity;
  initialData?: IAny;
  label: string;
  userHasUpdatePermission: boolean;
  onRelationshipUpdated?: () => void;
}

type SidebarMode = "none" | "add-existing" | "create-new";

const RelationshipCollectionField = ({
  field,
  entity,
  initialData,
  label,
  userHasUpdatePermission,
  onRelationshipUpdated,
}: RelationshipCollectionFieldProps) => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("none");
  const [nestedEntity, setNestedEntity] = useState<{
    entityName: string;
    entityId: number;
  } | null>(null);
  const { data: entities } = useEntities();
  const queryClient = useQueryClient();
  const formRef = useRef<EntityFormHandle>(null);
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  const { currentTenant } = useAuth();
  const confirm = useConfirm();
  const formik = useFormikContext<IAny>();

  const relationship = entity.source_relationships.find(
    (r) => r.id === field.relationship_id,
  );

  const isOneToMany =
    field.database_type === RelationshipFieldDatabaseType.ONE_TO_MANY;
  const isManyToMany =
    field.database_type === RelationshipFieldDatabaseType.MANY_TO_MANY;

  // For many-to-many relationships, we need the junction entity
  const junctionEntity = isManyToMany
    ? entities?.find((e) => e.id === relationship?.junction_entity_id)
    : null;

  // For one-to-many relationships, we need the target entity
  const targetEntity = entities?.find(
    (e) => e.id === relationship?.target_entity_id,
  );

  const formValue = formik?.values?.[field.name];
  const selectedIds = Array.isArray(formValue) ? formValue.map(id => Number(id)) : [];

  // Mutation for deleting relationships
  const deleteRelationshipMutation = useMutation({
    mutationFn: async (recordId: number) => {
      if (isManyToMany) {
        // Delete the junction record
        return anyService.deleteAny(junctionEntity!.name, recordId);
      } else {
        // Update the target record to remove the foreign key
        return anyService.updateAny(targetEntity!.name, recordId, {
          [relationship!.target_field_name]: null,
        });
      }
    },
    onSuccess: () => {
      // Invalidate queries for both source and target entities
      [
        entity.name,
        targetEntity?.name,
        isManyToMany ? junctionEntity!.name : null,
      ]
        .filter(Boolean)
        .forEach((entityName) => {
          queryClient.invalidateQueries({ queryKey: ["entities", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entities-data", entityName],
          });
          queryClient.invalidateQueries({ queryKey: ["entity", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entity-tree", entityName],
          });
        });
      onRelationshipUpdated?.();
    },
    onError: (error) => {
      console.error("Failed to delete relationship:", error);
    },
  });

  // Mutation for creating relationships
  const createRelationshipMutation = useMutation({
    mutationFn: async (targetRecordIds: number[]) => {
      if (isManyToMany) {
        // Create junction records for each selected record
        const promises = targetRecordIds.map((targetRecordId) =>
          anyService.createAny(junctionEntity!.name, {
            [`${entity.name}_id`]: initialData?.id,
            [`${targetEntity!.name}_id`]: targetRecordId,
          }),
        );
        return Promise.all(promises);
      } else {
        // Update target records' foreign keys
        const promises = targetRecordIds.map((targetRecordId) =>
          anyService.updateAny(targetEntity!.name, targetRecordId, {
            [relationship!.target_field_name]:
              initialData?.[relationship!.source_field_name],
          }),
        );
        return Promise.all(promises);
      }
    },
    onSuccess: () => {
      // Invalidate queries for both source and target entities
      [
        entity.name,
        targetEntity?.name,
        isManyToMany ? junctionEntity!.name : null,
      ]
        .filter(Boolean)
        .forEach((entityName) => {
          queryClient.invalidateQueries({ queryKey: ["entities", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entities-data", entityName],
          });
          queryClient.invalidateQueries({ queryKey: ["entity", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entity-tree", entityName],
          });
        });
      onRelationshipUpdated?.();
      setSidebarMode("none");
      setSelectedRecords([]);
    },
    onError: (error) => {
      console.error("Failed to create relationship:", error);
    },
  });

  // Mutation for creating new related records
  const createRelatedRecordMutation = useMutation({
    mutationFn: async (targetRecord: IAny) => {
      if (isManyToMany) {
        await anyService.createAny(junctionEntity!.name, {
          [`${entity.name}_id`]: initialData?.id,
          [`${targetEntity!.name}_id`]: targetRecord.id,
        });
        return targetRecord;
      }
    },
    onSuccess: () => {
      // Invalidate queries for both source and target entities
      [
        entity.name,
        targetEntity?.name,
        isManyToMany ? junctionEntity!.name : null,
      ]
        .filter(Boolean)
        .forEach((entityName) => {
          queryClient.invalidateQueries({ queryKey: ["entities", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entities-data", entityName],
          });
          queryClient.invalidateQueries({ queryKey: ["entity", entityName] });
          queryClient.invalidateQueries({
            queryKey: ["entity-tree", entityName],
          });
        });
      onRelationshipUpdated?.();
      setSidebarMode("none");
    },
    onError: (error) => {
      console.error("Failed to create related record:", error);
    },
  });

  // Don't show relationship controls in create mode or when entity doesn't exist yet,
  // unless it is a many-to-many relationship which we handle in the frontend form state
  if (!initialData?.id && !isManyToMany) {
    return null;
  }

  if (!relationship) {
    return <Text color="red">Invalid relationship configuration</Text>;
  }

  if (!targetEntity) {
    return <Text color="red">Target entity not found</Text>;
  }

  if (isManyToMany && !junctionEntity) {
    return (
      <Text color="red">
        Junction entity not found for many-to-many relationship
      </Text>
    );
  }

  // Calculate filter query for displaying related records
  const filterQuery = initialData?.id
    ? (isManyToMany
      ? `${entity.name}_id=${initialData?.id}`
      : `${relationship.target_field_name}=${
          initialData?.[relationship.source_field_name]
        }`)
    : (selectedIds.length > 0
      ? `id=IN:${selectedIds.join(",")}`
      : `id=IN:-1`);

  // Display entity name for the grid
  const displayEntityName = initialData?.id
    ? (isManyToMany ? junctionEntity!.name : targetEntity.name)
    : targetEntity.name;

  const handleExistingRecordsSelected = () => {
    if (selectedRecords.length > 0) {
      if (initialData?.id) {
        createRelationshipMutation.mutate(selectedRecords);
      } else {
        const rawVal = formik.values[field.name];
        const currentVal = Array.isArray(rawVal) ? (rawVal as any[]) : [];
        const newSelected = selectedRecords.map(id => Number(id));
        const updatedVal = Array.from(new Set([...currentVal, ...newSelected]));
        formik.setFieldValue(field.name, updatedVal);
        setSidebarMode("none");
        setSelectedRecords([]);
      }
    }
  };

  const handleDelete = async (recordId: number) => {
    const confirmed = await confirm({
      title: "Remove Relationship",
      message: "Are you sure you want to remove this relationship?",
      confirmText: "Remove",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      if (initialData?.id) {
        deleteRelationshipMutation.mutate(recordId);
      } else {
        const rawVal = formik.values[field.name];
        const currentVal = Array.isArray(rawVal) ? (rawVal as any[]) : [];
        const updatedVal = currentVal.filter((id: number) => id !== recordId);
        formik.setFieldValue(field.name, updatedVal);
      }
    }
  };

  const additionalActions = (data: IAny) => {
    if (!userHasUpdatePermission && initialData?.id) return null;

    return (
      <Button
        type="button"
        size="1"
        variant="ghost"
        color="red"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDelete(data.id);
        }}
        disabled={deleteRelationshipMutation.isPending}
        style={{
          minWidth: "auto",
          padding: "0",
        }}
      >
        <Trash2 size={16} />
      </Button>
    );
  };

  const getSidebarTitle = () => {
    switch (sidebarMode) {
      case "add-existing":
        return `Add Existing ${singularize(
          formatColumnName(targetEntity.name),
        )}`;
      case "create-new":
        return `Create New ${singularize(formatColumnName(targetEntity.name))}`;
      default:
        return "";
    }
  };

  return (
    <Box mb="6">
      <Flex align="center" justify="between" mb="3">
        <Text as="label" size="2" weight="medium" color="gray">
          {label}
        </Text>
        {(userHasUpdatePermission || !initialData?.id) && (
          <Flex gap="2" wrap="wrap">
            <Button
              type="button"
              size="1"
              variant="soft"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSidebarMode("add-existing");
                setSelectedRecords([]);
              }}
              disabled={createRelationshipMutation.isPending}
              style={{
                minWidth: "auto",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexShrink: 0,
              }}
            >
              <Link size={16} />
              <Text size="2">Add Existing</Text>
            </Button>
            <Button
              type="button"
              size="1"
              variant="soft"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSidebarMode("create-new");
              }}
              disabled={createRelatedRecordMutation.isPending}
              style={{
                minWidth: "auto",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexShrink: 0,
              }}
            >
              <Plus size={16} />
              <Text size="2">Create New</Text>
            </Button>
          </Flex>
        )}
      </Flex>

      <Box
        style={{
          border: "1px solid var(--gray-6)",
          borderRadius: "var(--radius-3)",
          overflow: "hidden",
        }}
      >
        <ManagedEntityGrid
          entityName={displayEntityName}
          filterQuery={filterQuery}
          disableFieldSelector
          rowSelection="none"
          onCellClicked={() => {}}
          additionalActions={additionalActions}
          linkTemplate={
            isManyToMany && initialData?.id
              ? (data) =>
                  `/org/${currentTenant?.id}/entities/${targetEntity.name}/${
                    (data[`${targetEntity.name}_id`] as Record<string, any>)?.id
                  }`
              : (data) => `/org/${currentTenant?.id}/entities/${targetEntity.name}/${data.id}`
          }
          onRowView={(data) => {
            const targetId =
              isManyToMany && initialData?.id
                ? (data[`${targetEntity.name}_id`] as Record<string, any>)?.id
                : data.id;
            if (targetId) {
              setNestedEntity({
                entityName: targetEntity.name,
                entityId: Number(targetId),
              });
            }
          }}
        />
      </Box>

      <SideDrawer
        isOpen={sidebarMode !== "none"}
        onClose={() => setSidebarMode("none")}
        title={getSidebarTitle()}
        width="max-w-xl"
        saveButton={
          sidebarMode === "create-new"
            ? {
                label: "Save",
                onClick: () => {
                  formRef.current?.saveChanges();
                },
                loading: createRelatedRecordMutation.isPending,
              }
            : sidebarMode === "add-existing"
              ? {
                  label: "Add Selected",
                  onClick: handleExistingRecordsSelected,
                  loading: createRelationshipMutation.isPending,
                  disabled: selectedRecords.length === 0,
                }
              : undefined
        }
      >
        {sidebarMode === "add-existing" && (
          <Box>
            <Text
              size="2"
              color="gray"
              mb="4"
              style={{ display: "block", lineHeight: "1.5" }}
            >
              Select existing{" "}
              <Text weight="medium" color="gray">
                {singularize(formatColumnName(targetEntity.name))}
              </Text>{" "}
              records to add to this relationship.
            </Text>
            <Box
              style={{
                border: "1px solid var(--gray-6)",
                borderRadius: "var(--radius-3)",
                overflow: "hidden",
              }}
            >
              <ManagedEntityGrid
                entityName={targetEntity.name}
                disableFieldSelector
                rowSelection="multiple"
                onCellClicked={() => {}}
                onSelectionChanged={(rows: IAny[]) => {
                  setSelectedRecords(rows.map((row) => row.id));
                }}
              />
            </Box>
          </Box>
        )}

        {sidebarMode === "create-new" && (
          <Box>
            <Text
              size="2"
              color="gray"
              mb="4"
              style={{ display: "block", lineHeight: "1.5" }}
            >
              Create a new{" "}
              <Text weight="medium" color="gray">
                {singularize(formatColumnName(targetEntity.name))}
              </Text>{" "}
              and add it to this relationship.
            </Text>
            <EntityFormV2
              ref={formRef}
              entity={targetEntity}
              mode="create"
              userHasUpdatePermission={true}
              splitFieldsIntoGrid={false}
              initialData={
                isOneToMany
                  ? ({
                      [relationship.target_field_name]:
                        initialData?.[relationship.source_field_name],
                    } as IAny)
                  : undefined
              }
              onCreateSuccess={(createdRecord) => {
                if (initialData?.id) {
                  if (isManyToMany) {
                    // Create the junction record after the target record is created
                    createRelatedRecordMutation.mutate(createdRecord);
                  }
                } else {
                  const rawVal = formik.values[field.name];
                  const currentVal = Array.isArray(rawVal) ? (rawVal as any[]) : [];
                  const updatedVal = Array.from(new Set([...currentVal, Number(createdRecord.id)]));
                  formik.setFieldValue(field.name, updatedVal);
                }
                setSidebarMode("none");
              }}
            />
          </Box>
        )}
      </SideDrawer>

      {nestedEntity && (
        <SideDrawer
          isOpen={true}
          onClose={() => setNestedEntity(null)}
          title={`${singularize(formatColumnName(nestedEntity.entityName))} Details`}
          width="max-w-2xl"
        >
          <EntityDetailView
            entityName={nestedEntity.entityName}
            entityId={nestedEntity.entityId}
          />
        </SideDrawer>
      )}
    </Box>
  );
};

export default RelationshipCollectionField;
